import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Plus,
  X,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'

import type { Task, TaskStatus } from '@/lib/types/types'
import type React from 'react'

import { useDocumentTitle } from '@/hooks/use-document-title'
import { useDrivers, useTasks, useVehicles } from '@/hooks/api-hooks'
import { formatDateTime } from '@/lib/format'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/tasks/')({
  head: () => ({ meta: [{ title: localizedPageTitle('tasks') }] }),
  component: TasksPage,
})

export default function TasksPage() {
  useDocumentTitle('tasks')
  const { data: tasks, isLoading } = useTasks()
  const { data: drivers } = useDrivers()
  const { data: vehicles } = useVehicles()
  const [activeTab, setActiveTab] = useState<TaskStatus | 'all'>('all')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)

  const getDriverName = (driverId: string) => {
    return (
      drivers?.find((d: any) => d.id === Number(driverId))?.name || 'Unknown'
    )
  }

  const getVehiclePlate = (vehicleId: string) => {
    return vehicles?.find((v) => v.id === vehicleId)?.licensePlate || 'Unknown'
  }

  const handleStartTask = (taskId: string) => {
    alert(
      `Task ${taskId} started! (This would update the status to "in-progress" in a real application)`,
    )
  }

  const handleCompleteTask = (taskId: string) => {
    alert(
      `Task ${taskId} completed! (This would update the status to "completed" in a real application)`,
    )
  }

  const handleEditTask = (task: Task) => {
    setEditingTask(task)
  }

  const filteredTasks = tasks?.filter(
    (task) => activeTab === 'all' || task.status === activeTab,
  )

  const taskCounts = {
    all: tasks?.length || 0,
    pending: tasks?.filter((t) => t.status === 'pending').length || 0,
    'in-progress': tasks?.filter((t) => t.status === 'in-progress').length || 0,
    completed: tasks?.filter((t) => t.status === 'completed').length || 0,
    cancelled: tasks?.filter((t) => t.status === 'cancelled').length || 0,
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tasks</h1>
          <p className="text-muted-foreground mt-1">
            Manage fleet tasks and assignments
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Task
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taskCounts.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <AlertCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {taskCounts['in-progress']}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taskCounts.completed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
            <XCircle className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taskCounts.cancelled}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Tasks</CardTitle>
          <CardDescription>Filter and manage tasks by status</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TaskStatus | 'all')}
          >
            <TabsList>
              <TabsTrigger value="all">All ({taskCounts.all})</TabsTrigger>
              <TabsTrigger value="pending">
                Pending ({taskCounts.pending})
              </TabsTrigger>
              <TabsTrigger value="in-progress">
                In Progress ({taskCounts['in-progress']})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({taskCounts.completed})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              ) : filteredTasks && filteredTasks.length > 0 ? (
                <div className="space-y-3">
                  {filteredTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      driverName={getDriverName(task.assignedTo)}
                      vehiclePlate={getVehiclePlate(task.vehicleId)}
                      onEdit={handleEditTask}
                      onStart={handleStartTask}
                      onComplete={handleCompleteTask}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <p className="text-muted-foreground text-sm">
                    No tasks found
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <CreateTaskDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        drivers={drivers || []}
        vehicles={vehicles || []}
      />

      {editingTask && (
        <EditTaskDialog
          task={editingTask}
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          drivers={drivers || []}
          vehicles={vehicles || []}
        />
      )}
    </div>
  )
}

function TaskCard({
  task,
  driverName,
  vehiclePlate,
  onEdit,
  onStart,
  onComplete,
}: {
  task: Task
  driverName: string
  vehiclePlate: string
  onEdit: (task: Task) => void
  onStart: (taskId: string) => void
  onComplete: (taskId: string) => void
}) {
  const isOverdue =
    new Date(task.dueDate) < new Date() && task.status !== 'completed'

  return (
    <div className="border-border hover:bg-muted/50 rounded-lg border p-4 transition-colors">
      <div className="mb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h3 className="font-semibold">{task.title}</h3>
            <Badge
              variant={
                task.priority === 'urgent'
                  ? 'destructive'
                  : task.priority === 'high'
                    ? 'default'
                    : task.priority === 'medium'
                      ? 'secondary'
                      : 'outline'
              }
              className="text-xs"
            >
              {task.priority}
            </Badge>
            <Badge
              variant={
                task.status === 'completed'
                  ? 'default'
                  : task.status === 'in-progress'
                    ? 'secondary'
                    : task.status === 'cancelled'
                      ? 'outline'
                      : 'outline'
              }
              className="text-xs"
            >
              {task.status}
            </Badge>
          </div>
          <p className="text-muted-foreground mb-2 text-sm">
            {task.description}
          </p>
          <div className="text-muted-foreground flex items-center gap-4 text-xs">
            <span>Assigned to: {driverName}</span>
            <span>Vehicle: {vehiclePlate}</span>
            {task.location && <span>Location: {task.location}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <Clock className="h-3 w-3" />
          <span
            className={
              isOverdue
                ? 'text-destructive font-medium'
                : 'text-muted-foreground'
            }
          >
            Due: {formatDateTime(task.dueDate)}
            {isOverdue && ' (Overdue)'}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(task)}>
            Edit
          </Button>
          {task.status === 'pending' && (
            <Button size="sm" onClick={() => onStart(task.id)}>
              Start Task
            </Button>
          )}
          {task.status === 'in-progress' && (
            <Button size="sm" onClick={() => onComplete(task.id)}>
              Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function CreateTaskDialog({
  open,
  onOpenChange,
  drivers,
  vehicles,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  drivers: Array<any>
  vehicles: Array<any>
}) {
  const [formData, setFormData] = useState({
    taskName: '',
    taskTag: '',
    clientAddress: '',
    priority: 'Normal',
    checkpoints: [
      {
        address: '',
        type: 'Loading',
        scheduledDateTime: '',
        contactPerson: '',
        specialInstructions: '',
      },
    ],
    temperatureValue: '',
    temperatureMode: 'C/S',
    cargoWeight: '',
    cargoVolume: '',
    specialRequirements: '',
    assignedDriverId: '',
    assignedVehicleId: '',
    startDate: '',
    expectedCompletionDate: '',
    integrationId: '',
    timeComment: '',
    notesToDriver: '',
    driverFileTags: '',
  })

  const addCheckpoint = () => {
    setFormData({
      ...formData,
      checkpoints: [
        ...formData.checkpoints,
        {
          address: '',
          type: 'Unloading',
          scheduledDateTime: '',
          contactPerson: '',
          specialInstructions: '',
        },
      ],
    })
  }

  const removeCheckpoint = (index: number) => {
    if (formData.checkpoints.length > 1) {
      setFormData({
        ...formData,
        checkpoints: formData.checkpoints.filter((_, i) => i !== index),
      })
    }
  }

  const updateCheckpoint = (index: number, field: string, value: string) => {
    const updated = [...formData.checkpoints]
    updated[index] = { ...updated[index], [field]: value }
    setFormData({ ...formData, checkpoints: updated })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[1254px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Create a comprehensive task with multi-checkpoint route
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taskName">Task Name *</Label>
                <Input
                  id="taskName"
                  required
                  value={formData.taskName}
                  onChange={(e) =>
                    setFormData({ ...formData, taskName: e.target.value })
                  }
                  placeholder="Delivery to Munich"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="taskTag">Task Tag *</Label>
                <Select
                  value={formData.taskTag}
                  onValueChange={(value) =>
                    setFormData({ ...formData, taskTag: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select task type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General cargo</SelectItem>
                    <SelectItem value="Refrigerated">
                      Refrigerated (reefer)
                    </SelectItem>
                    <SelectItem value="Hazardous">
                      Hazardous materials (ADR)
                    </SelectItem>
                    <SelectItem value="Oversized">Oversized load</SelectItem>
                    <SelectItem value="Express">Express delivery</SelectItem>
                    <SelectItem value="International">
                      International transport
                    </SelectItem>
                    <SelectItem value="Domestic">Domestic transport</SelectItem>
                    <SelectItem value="Fragile">Fragile goods</SelectItem>
                    <SelectItem value="Perishable">Perishable goods</SelectItem>
                    <SelectItem value="Construction">
                      Construction materials
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientAddress">Client or Address</Label>
                <Input
                  id="clientAddress"
                  value={formData.clientAddress}
                  onChange={(e) =>
                    setFormData({ ...formData, clientAddress: e.target.value })
                  }
                  placeholder="Client name or address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    setFormData({ ...formData, priority: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Route & Checkpoints</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCheckpoint}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Checkpoint
              </Button>
            </div>

            {formData.checkpoints.map((checkpoint, index) => (
              <Card key={index}>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <div className="mb-2 flex items-center justify-between">
                      <Badge variant="outline">Checkpoint {index + 1}</Badge>
                      {formData.checkpoints.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCheckpoint(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Address *</Label>
                        <Input
                          required
                          value={checkpoint.address}
                          onChange={(e) =>
                            updateCheckpoint(index, 'address', e.target.value)
                          }
                          placeholder="Enter checkpoint address"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={checkpoint.type}
                          onValueChange={(value) =>
                            updateCheckpoint(index, 'type', value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Loading">Loading</SelectItem>
                            <SelectItem value="Unloading">Unloading</SelectItem>
                            <SelectItem value="Border">Border</SelectItem>
                            <SelectItem value="Customs">Customs</SelectItem>
                            <SelectItem value="Fuel">Fuel</SelectItem>
                            <SelectItem value="Rest">Rest</SelectItem>
                            <SelectItem value="Warehouse">Warehouse</SelectItem>
                            <SelectItem value="Client">Client</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Scheduled Date/Time</Label>
                        <Input
                          type="datetime-local"
                          value={checkpoint.scheduledDateTime}
                          onChange={(e) =>
                            updateCheckpoint(
                              index,
                              'scheduledDateTime',
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Contact Person</Label>
                        <Input
                          value={checkpoint.contactPerson}
                          onChange={(e) =>
                            updateCheckpoint(
                              index,
                              'contactPerson',
                              e.target.value,
                            )
                          }
                          placeholder="Contact name"
                        />
                      </div>

                      <div className="col-span-2 space-y-2">
                        <Label>Special Instructions</Label>
                        <Input
                          value={checkpoint.specialInstructions}
                          onChange={(e) =>
                            updateCheckpoint(
                              index,
                              'specialInstructions',
                              e.target.value,
                            )
                          }
                          placeholder="Any special instructions for this checkpoint"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Transport Details</h3>
            <div className="grid grid-cols-2 gap-4">
              {formData.taskTag === 'Refrigerated' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="temperatureValue">Temperature (°C)</Label>
                    <Input
                      id="temperatureValue"
                      type="number"
                      step="0.1"
                      value={formData.temperatureValue}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          temperatureValue: e.target.value,
                        })
                      }
                      placeholder="-18"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="temperatureMode">Temperature Mode</Label>
                    <Select
                      value={formData.temperatureMode}
                      onValueChange={(value) =>
                        setFormData({ ...formData, temperatureMode: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="C/S">C/S (Celsius/Start)</SelectItem>
                        <SelectItem value="Cont.">
                          Cont. (Continuous)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="cargoWeight">Cargo Weight (tons)</Label>
                <Input
                  id="cargoWeight"
                  type="number"
                  step="0.1"
                  value={formData.cargoWeight}
                  onChange={(e) =>
                    setFormData({ ...formData, cargoWeight: e.target.value })
                  }
                  placeholder="15.5"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargoVolume">Cargo Volume (m³)</Label>
                <Input
                  id="cargoVolume"
                  type="number"
                  step="0.1"
                  value={formData.cargoVolume}
                  onChange={(e) =>
                    setFormData({ ...formData, cargoVolume: e.target.value })
                  }
                  placeholder="45"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="specialRequirements">
                  Special Requirements
                </Label>
                <Textarea
                  id="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      specialRequirements: e.target.value,
                    })
                  }
                  placeholder="Any special handling or requirements..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Assignment</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assignedDriverId">Assign Driver</Label>
                <Select
                  value={formData.assignedDriverId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, assignedDriverId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignedVehicleId">Assign Vehicle</Label>
                <Select
                  value={formData.assignedVehicleId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, assignedVehicleId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.plateNumber} - {vehicle.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Timing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date/Time</Label>
                <Input
                  id="startDate"
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedCompletionDate">
                  Expected Completion
                </Label>
                <Input
                  id="expectedCompletionDate"
                  type="datetime-local"
                  value={formData.expectedCompletionDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expectedCompletionDate: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Additional Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="integrationId">Integration ID</Label>
                <Input
                  id="integrationId"
                  value={formData.integrationId}
                  onChange={(e) =>
                    setFormData({ ...formData, integrationId: e.target.value })
                  }
                  placeholder="External system reference"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeComment">Time Comment</Label>
                <Input
                  id="timeComment"
                  value={formData.timeComment}
                  onChange={(e) =>
                    setFormData({ ...formData, timeComment: e.target.value })
                  }
                  placeholder="Additional timing notes"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label htmlFor="notesToDriver">Notes to Driver</Label>
                <Textarea
                  id="notesToDriver"
                  value={formData.notesToDriver}
                  onChange={(e) =>
                    setFormData({ ...formData, notesToDriver: e.target.value })
                  }
                  placeholder="Important instructions for the driver..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="driverFileTags">Driver File Tags</Label>
                <Input
                  id="driverFileTags"
                  value={formData.driverFileTags}
                  onChange={(e) =>
                    setFormData({ ...formData, driverFileTags: e.target.value })
                  }
                  placeholder="Comma-separated tags"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Create Task</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditTaskDialog({
  task,
  open,
  onOpenChange,
  drivers,
  vehicles,
}: {
  task: Task
  open: boolean
  onOpenChange: (open: boolean) => void
  drivers: Array<any>
  vehicles: Array<any>
}) {
  const [formData, setFormData] = useState({
    title: task.title,
    description: task.description,
    priority: task.priority,
    status: task.status,
    assignedTo: task.assignedTo,
    vehicleId: task.vehicleId,
    dueDate: new Date(task.dueDate).toISOString().slice(0, 16),
    location: task.location || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    alert(`Task "${formData.title}" updated successfully!`)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>
            Update task details and assignments
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Task Title *</Label>
            <Input
              id="edit-title"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value as any })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value as TaskStatus })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-driver">Assigned Driver</Label>
              <Select
                value={formData.assignedTo}
                onValueChange={(value) =>
                  setFormData({ ...formData, assignedTo: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-vehicle">Assigned Vehicle</Label>
              <Select
                value={formData.vehicleId}
                onValueChange={(value) =>
                  setFormData({ ...formData, vehicleId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.plateNumber} - {vehicle.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-dueDate">Due Date</Label>
              <Input
                id="edit-dueDate"
                type="datetime-local"
                value={formData.dueDate}
                onChange={(e) =>
                  setFormData({ ...formData, dueDate: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="Task location"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
